import type { AuthResponse, AuthUser, RefreshResponse, TokenService } from '@/types';
import { authResponseSchema, authUserSchema, refreshResponseSchema } from '@/types';
import { createApiError, makeRequest, isApiError } from '@/lib/api/http-client';
import { getLogger } from '@/lib/logger';

const logger = getLogger({ scope: 'auth.token-service' });

export const AUTH_ENDPOINTS = {
  EXCHANGE: '/auth/exchange',
  LOGOUT: '/auth/logout',
  LOGOUT_ALL: '/auth/logout-all',
  REFRESH: '/auth/refresh',
  ME: '/auth/me',
} as const;

/**
 * Token service implementation for authentication operations
 */
class TokenServiceImpl implements TokenService {
  /**
   * Exchange original token for auth and refresh tokens (cookies set by server)
   */
  async exchangeToken(originalToken: string, platform: string): Promise<AuthResponse> {
    // Validate inputs early to avoid wrapping simple validation errors
    if (!originalToken || typeof originalToken !== 'string') {
      throw new Error('Original token is required and must be a string');
    }

    if (!platform || typeof platform !== 'string') {
      throw new Error('Platform is required for token exchange');
    }

    try {
      const body: Record<string, unknown> = { token: originalToken, platform };

      const response = await makeRequest<AuthResponse>(
        { method: 'POST', url: AUTH_ENDPOINTS.EXCHANGE, data: body },
        { retries: 2, retryDelay: 1000 }
      );

      if (!response.success || !response.data) {
        throw createApiError({
          message: response.message || 'Token exchange failed',
          status: 400,
          code: 'TOKEN_EXCHANGE_FAILED',
        });
      }

      const parsed = authResponseSchema.safeParse(response.data);

      if (!parsed.success) {
        throw createApiError({
          message: `Invalid token response format: ${parsed.error.message}`,
          status: 400,
          code: 'INVALID_TOKEN_RESPONSE',
        });
      }

      return parsed.data;
    } catch (error) {
      logger.error('Token exchange failed', { error });

      if (isApiError(error)) {
        throw error; // preserve API error shape
      }

      throw createApiError({
        message: error instanceof Error ? error.message : 'Token exchange failed',
        status: 500,
        code: 'TOKEN_EXCHANGE_ERROR',
      });
    }
  }

  /**
   * Refresh auth token. No body needed — refresh token is sent via cookie.
   * Returns only { expiresIn }. Use fetchCurrentUser() if user data is needed.
   */
  async refreshAuthToken(): Promise<RefreshResponse> {
    try {
      const response = await makeRequest<RefreshResponse>(
        { method: 'POST', url: AUTH_ENDPOINTS.REFRESH, skipAuthRetry: true },
        {
          retries: 1, // Only retry once for refresh requests
          retryDelay: 500,
        }
      );

      if (!response.success || !response.data) {
        throw createApiError({
          message: response.message || 'Token refresh failed',
          status: 401,
          code: 'TOKEN_REFRESH_FAILED',
        });
      }

      const parsed = refreshResponseSchema.safeParse(response.data);

      if (!parsed.success) {
        throw createApiError({
          message: `Invalid refresh response format: ${parsed.error.message}`,
          status: 400,
          code: 'INVALID_REFRESH_RESPONSE',
        });
      }

      return parsed.data;
    } catch (error) {
      logger.error('Token refresh failed', { error });

      if (isApiError(error)) {
        throw error; // preserve API error shape
      }

      throw createApiError({
        message: error instanceof Error ? error.message : 'Token refresh failed',
        status: 500,
        code: 'TOKEN_REFRESH_ERROR',
      });
    }
  }

  /**
   * Fetch current authenticated user's profile from /auth/me
   */
  async fetchCurrentUser(): Promise<AuthUser> {
    try {
      const response = await makeRequest<AuthUser>(
        { method: 'GET', url: AUTH_ENDPOINTS.ME, skipAuthRetry: true },
        { retries: 1, retryDelay: 500 }
      );

      if (!response.success || !response.data) {
        throw createApiError({
          message: response.message || 'Failed to fetch user',
          status: 401,
          code: 'FETCH_USER_FAILED',
        });
      }

      const parsed = authUserSchema.safeParse(response.data);

      if (!parsed.success) {
        throw createApiError({
          message: `Invalid user response format: ${parsed.error.message}`,
          status: 400,
          code: 'INVALID_USER_RESPONSE',
        });
      }

      return parsed.data;
    } catch (error) {
      logger.error('Fetch current user failed', { error });

      if (isApiError(error)) {
        throw error;
      }

      throw createApiError({
        message: error instanceof Error ? error.message : 'Failed to fetch user',
        status: 500,
        code: 'FETCH_USER_ERROR',
      });
    }
  }

  /**
   * Logout and invalidate tokens on the server
   */
  async logout(): Promise<void> {
    try {
      await makeRequest(
        { method: 'POST', url: AUTH_ENDPOINTS.LOGOUT, skipAuthRetry: true },
        { retries: 1, retryDelay: 500 }
      );
    } catch (error) {
      // Log but don't throw - logout should always succeed locally
      logger.warn('Server logout failed', { error });
    }
  }

  /**
   * Logout from all devices. Server identifies the user via auth cookie.
   */
  async logoutAll(): Promise<void> {
    try {
      await makeRequest(
        { method: 'POST', url: AUTH_ENDPOINTS.LOGOUT_ALL },
        { retries: 1, retryDelay: 500 }
      );
    } catch (error) {
      logger.warn('Server logout-all failed', { error });
    }
  }
}

/**
 * Singleton token service instance
 */
export const tokenService = new TokenServiceImpl();
